// Drawing App - Shape Detection & Snap

import { Point, ShapeType } from './types';

interface DetectedShape {
  type: ShapeType | 'circle';
  points: { x: number; y: number }[];
  confidence: number;
}

export class ShapeDetector {
  private enabled = true;
  private confidenceThreshold = 0.7;

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  detect(points: Point[]): DetectedShape | null {
    if (!this.enabled || points.length < 5) return null;

    // Try each shape type and return the best match
    const candidates: DetectedShape[] = [];

    const lineResult = this.detectLine(points);
    if (lineResult) candidates.push(lineResult);

    const circleResult = this.detectCircle(points);
    if (circleResult) candidates.push(circleResult);

    const rectResult = this.detectRectangle(points);
    if (rectResult) candidates.push(rectResult);

    const triResult = this.detectTriangle(points);
    if (triResult) candidates.push(triResult);

    if (candidates.length === 0) return null;

    // Return highest confidence match
    candidates.sort((a, b) => b.confidence - a.confidence);
    return candidates[0].confidence >= this.confidenceThreshold ? candidates[0] : null;
  }

  private detectLine(points: Point[]): DetectedShape | null {
    const first = points[0];
    const last = points[points.length - 1];
    const lineLen = Math.hypot(last.x - first.x, last.y - first.y);

    if (lineLen < 20) return null;

    // Calculate average deviation from the line
    let totalDeviation = 0;
    for (const p of points) {
      const d = this.pointToLineDistance(p, first, last);
      totalDeviation += d;
    }
    const avgDeviation = totalDeviation / points.length;
    const confidence = Math.max(0, 1 - avgDeviation / (lineLen * 0.1));

    if (confidence < this.confidenceThreshold) return null;

    return {
      type: 'line',
      points: [{ x: first.x, y: first.y }, { x: last.x, y: last.y }],
      confidence
    };
  }

  private detectCircle(points: Point[]): DetectedShape | null {
    // Find bounding box center and average radius
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of points) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }

    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const avgRadius = points.reduce((sum, p) => sum + Math.hypot(p.x - cx, p.y - cy), 0) / points.length;

    if (avgRadius < 10) return null;

    // Check if stroke is closed
    const first = points[0];
    const last = points[points.length - 1];
    const closedness = Math.hypot(last.x - first.x, last.y - first.y) / avgRadius;
    if (closedness > 0.5) return null;

    // Check deviation from circle
    let totalDeviation = 0;
    for (const p of points) {
      const dist = Math.hypot(p.x - cx, p.y - cy);
      totalDeviation += Math.abs(dist - avgRadius);
    }
    const avgDeviation = totalDeviation / points.length;
    const confidence = Math.max(0, 1 - avgDeviation / (avgRadius * 0.15));

    if (confidence < this.confidenceThreshold) return null;

    // Check aspect ratio (should be close to 1 for circle)
    const width = maxX - minX;
    const height = maxY - minY;
    const aspectRatio = Math.min(width, height) / Math.max(width, height);
    if (aspectRatio < 0.7) return null;

    return {
      type: 'circle',
      points: [{ x: cx, y: cy }, { x: cx + avgRadius, y: cy }],
      confidence: confidence * aspectRatio
    };
  }

  private detectRectangle(points: Point[]): DetectedShape | null {
    // Check if stroke is closed
    const first = points[0];
    const last = points[points.length - 1];
    const totalLen = this.pathLength(points);
    const closedness = Math.hypot(last.x - first.x, last.y - first.y) / totalLen;
    if (closedness > 0.15) return null;

    // Find corners using angle changes
    const corners = this.findCorners(points);
    if (corners.length < 4 || corners.length > 6) return null;

    // Use bounding box as the snapped rectangle
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of points) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }

    const width = maxX - minX;
    const height = maxY - minY;
    if (width < 15 || height < 15) return null;

    // Check how well points fit the rectangle
    let totalDeviation = 0;
    for (const p of points) {
      const dLeft = Math.abs(p.x - minX);
      const dRight = Math.abs(p.x - maxX);
      const dTop = Math.abs(p.y - minY);
      const dBottom = Math.abs(p.y - maxY);
      const minDist = Math.min(dLeft, dRight, dTop, dBottom);
      totalDeviation += minDist;
    }
    const avgDeviation = totalDeviation / points.length;
    const confidence = Math.max(0, 1 - avgDeviation / (Math.min(width, height) * 0.2));

    if (confidence < this.confidenceThreshold) return null;

    return {
      type: 'rectangle',
      points: [
        { x: minX, y: minY },
        { x: maxX, y: minY },
        { x: maxX, y: maxY },
        { x: minX, y: maxY }
      ],
      confidence
    };
  }

  private detectTriangle(points: Point[]): DetectedShape | null {
    const first = points[0];
    const last = points[points.length - 1];
    const totalLen = this.pathLength(points);
    const closedness = Math.hypot(last.x - first.x, last.y - first.y) / totalLen;
    if (closedness > 0.15) return null;

    const corners = this.findCorners(points);
    if (corners.length < 3 || corners.length > 4) return null;

    // Take first 3 corners
    const triPoints = corners.slice(0, 3).map(i => ({ x: points[i].x, y: points[i].y }));

    // Check area is significant
    const area = Math.abs(
      (triPoints[1].x - triPoints[0].x) * (triPoints[2].y - triPoints[0].y) -
      (triPoints[2].x - triPoints[0].x) * (triPoints[1].y - triPoints[0].y)
    ) / 2;

    if (area < 200) return null;

    return {
      type: 'triangle',
      points: triPoints,
      confidence: 0.75
    };
  }

  private findCorners(points: Point[]): number[] {
    const corners: number[] = [0];
    const windowSize = Math.max(5, Math.floor(points.length / 10));

    for (let i = windowSize; i < points.length - windowSize; i++) {
      const prev = points[i - windowSize];
      const curr = points[i];
      const next = points[i + windowSize];

      const angle = this.angleBetween(prev, curr, next);
      if (angle < Math.PI * 0.65) {
        // Check if too close to previous corner
        if (corners.length === 0 || i - corners[corners.length - 1] > windowSize) {
          corners.push(i);
        }
      }
    }

    corners.push(points.length - 1);
    return corners;
  }

  private angleBetween(a: Point, b: Point, c: Point): number {
    const ba = { x: a.x - b.x, y: a.y - b.y };
    const bc = { x: c.x - b.x, y: c.y - b.y };
    const dot = ba.x * bc.x + ba.y * bc.y;
    const magBA = Math.hypot(ba.x, ba.y);
    const magBC = Math.hypot(bc.x, bc.y);
    if (magBA === 0 || magBC === 0) return Math.PI;
    return Math.acos(Math.max(-1, Math.min(1, dot / (magBA * magBC))));
  }

  private pointToLineDistance(p: Point, a: Point, b: Point): number {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
    const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq));
    return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
  }

  private pathLength(points: Point[]): number {
    let len = 0;
    for (let i = 1; i < points.length; i++) {
      len += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
    }
    return len;
  }
}
